<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useConversationSummary } from 'dashboard/composables/useConversationSummary';

// Resumo de conversa com IA dentro do diálogo do board.
//
// O QUE ESTA TELA É OBRIGADA A DIZER, e não é cosmético: a PROSA do resumo não
// é verificada — só as citações são. A ancoragem prova que o trecho foi
// COPIADO daquela mensagem; ela não prova nada sobre a INTERPRETAÇÃO. O modelo
// pode escrever "cliente aceitou o acordo" citando corretamente uma mensagem
// que diz "vou pensar até sexta". Apresentar o resumo como fato verificado
// seria mentir, e por isso o aviso acompanha TODO resumo, não só o parcial.
//
// Os `descartes` da verificação NÃO são renderizados de propósito: são saída
// não verificada do modelo (inclusive fabricada), e mostrá-los com aparência
// de trecho da conversa daria voz de cliente a algo que nunca foi dito. As
// CONTAGENS aparecem, porque são a medida da alucinação.
const props = defineProps({
  conversationDisplayId: { type: Number, required: true },
});

const emit = defineEmits(['goToMessage', 'close']);

const { t } = useI18n();

const {
  isFeatureEnabled,
  summary,
  isLoading,
  errorKey,
  isPartial,
  citations,
  verification,
  isInputTruncated,
  retryAfterSeconds,
  isRetryBlocked,
  canGenerate,
  generate,
  load,
  generatedAt,
  generatedBy,
  newMessagesSince,
} = useConversationSummary(() => props.conversationDisplayId);

// Carrega o que já existe assim que o painel monta: é o que faz ele abrir
// pronto em vez de cobrar o LLM de novo a cada abertura.
onMounted(load);

const formattedGeneratedAt = computed(() =>
  generatedAt.value
    ? new Date(generatedAt.value).toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : ''
);

const actionLabel = computed(() =>
  summary.value ? t('KANBAN.SUMMARY.REGENERATE') : t('KANBAN.SUMMARY.GENERATE')
);

const verificationLabel = computed(() => {
  const data = verification.value;
  if (!data) return '';
  return t('KANBAN.SUMMARY.VERIFICATION', {
    verified: data.verificadas ?? 0,
    proposed: data.propostas ?? 0,
    discarded: data.descartadas ?? 0,
  });
});
</script>

<template>
  <!-- PAINEL, e não faixa em cima da conversa. Enquanto estava no fluxo da
       coluna principal, um resumo longo empurrava a thread até o piso de 128px
       dela e a conversa sumia — relatado em produção em 2026-08-20. Aqui ele
       ocupa uma lateral própria, como o painel de detalhe do DEBTHUB do outro
       lado, e a conversa fica intacta. -->
  <section
    v-if="isFeatureEnabled"
    id="kanban-summary-panel"
    data-testid="kanban-summary"
    role="region"
    :aria-label="t('KANBAN.SUMMARY.TITLE')"
    class="flex flex-col min-h-0"
  >
    <div
      class="flex items-center justify-between gap-2 px-4 py-3 border-b shrink-0 border-n-weak"
    >
      <h3 class="m-0 text-xs font-semibold uppercase text-n-slate-11">
        {{ t('KANBAN.SUMMARY.TITLE') }}
      </h3>
      <NextButton
        slate
        ghost
        xs
        icon="i-lucide-x"
        data-testid="kanban-summary-close"
        :aria-label="t('KANBAN.SUMMARY.CLOSE')"
        @click="emit('close')"
      />
    </div>

    <!-- Corpo com rolagem PRÓPRIA: o cabeçalho e o botão de gerar continuam
         visíveis por mais longo que seja o resumo. -->
    <div class="flex flex-col flex-1 min-h-0 gap-2 p-4 overflow-y-auto">
      <!-- Retry-After desabilita o botão pelo intervalo em vez de deixar o
           agente repetir na hora uma chamada que já se sabe que vai falhar. -->
      <NextButton
        slate
        faded
        xs
        class="self-start"
        data-testid="kanban-summary-generate"
        :is-loading="isLoading"
        :disabled="!canGenerate"
        :label="actionLabel"
        @click="generate"
      />

      <!-- QUANDO e por QUEM. Um resumo de ontem apresentado como atual é pior
           que nenhum resumo; `mensagens_novas` diz o tamanho do que ele não viu. -->
      <p
        v-if="summary && generatedAt"
        data-testid="kanban-summary-provenance"
        class="m-0 text-xs text-n-slate-11"
      >
        {{
          generatedBy
            ? t('KANBAN.SUMMARY.GENERATED_BY', {
                name: generatedBy,
                date: formattedGeneratedAt,
              })
            : t('KANBAN.SUMMARY.GENERATED_AT', { date: formattedGeneratedAt })
        }}
      </p>
      <p
        v-if="newMessagesSince > 0"
        data-testid="kanban-summary-stale"
        class="m-0 flex items-start gap-1 text-xs text-n-amber-11"
      >
        <span
          aria-hidden="true"
          class="i-lucide-triangle-alert size-3 shrink-0 mt-0.5"
        />
        {{ t('KANBAN.SUMMARY.STALE', { count: newMessagesSince }) }}
      </p>

      <p v-if="isLoading" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('KANBAN.SUMMARY.LOADING') }}
      </p>

      <div v-if="errorKey" role="alert" class="flex flex-col gap-1">
        <p class="m-0 flex items-start gap-1 text-xs text-n-ruby-11">
          <span
            aria-hidden="true"
            class="i-lucide-triangle-alert size-3 shrink-0 mt-0.5"
          />
          {{ t(`KANBAN.SUMMARY.ERRORS.${errorKey}`) }}
        </p>
        <p v-if="isRetryBlocked" class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.SUMMARY.RETRY_IN', { seconds: retryAfterSeconds }) }}
        </p>
      </div>

      <!-- ROLAGEM PRÓPRIA, e teto na seção: o resumo vive FORA do contêiner que
         rola a conversa (ver o comentário no modal), então sem teto ele empurra
         a thread até o piso de 128px dela e a conversa some — que é exatamente o
         que foi relatado em produção em 2026-08-20 com um resumo longo e cinco
         citações. Com `max-h-[38vh]` mais este `overflow-y-auto`, o resumo
         cresce até um limite e passa a rolar dentro de si; o cabeçalho e o botão
         de gerar continuam visíveis porque estão fora desta caixa. -->
      <div
        v-if="summary"
        data-testid="kanban-summary-result"
        role="status"
        class="flex flex-col min-h-0 gap-2 overflow-y-auto"
      >
        <!-- O aviso acompanha TODO resumo — a prosa e' saida nao verificada do
             modelo, e apresenta-la como fato seria mentir. O que mudou e' o CUSTO
             DE ESPACO: uma linha sempre visivel, e a explicacao inteira atras de
             um `details`. A garantia continua a mesma (ninguem le o resumo sem ver
             o aviso); o bloco de quatro linhas e' que comia o painel. -->
        <details
          data-testid="kanban-summary-not-verified"
          class="p-1.5 text-xs rounded-lg text-n-slate-12 bg-n-amber-3"
        >
          <!-- `marker:hidden` saiu: e' codigo morto — `display` nao e' propriedade
               aceita em `::marker` (verificado por getComputedStyle em
               2026-08-20), e quem esconde o triangulo e' o `display:flex` deste
               elemento. `items-start` para o icone acompanhar a PRIMEIRA linha
               quando o texto quebra em duas, em vez de flutuar no meio. -->
          <summary
            class="flex items-start gap-1 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
          >
            <span
              aria-hidden="true"
              class="i-lucide-triangle-alert size-3 shrink-0"
            />
            <!-- Uma frase só: o "entenda" flui COM o texto em vez de virar
                 bloco próprio à direita. Com `shrink-0` e o texto quebrando em
                 duas linhas (medido em 360px), ele ficava centralizado na
                 direita, órfão da frase que qualifica. -->
            <span class="min-w-0">
              {{ t('KANBAN.SUMMARY.NOT_VERIFIED_SHORT') }}
              <span class="underline text-n-slate-11">{{
                t('KANBAN.SUMMARY.NOT_VERIFIED_MORE')
              }}</span>
            </span>
          </summary>
          <p class="m-0 mt-1.5">
            {{ t('KANBAN.SUMMARY.NOT_VERIFIED_WARNING') }}
          </p>
        </details>

        <p
          v-if="isPartial"
          data-testid="kanban-summary-partial"
          class="m-0 flex items-start gap-1 text-xs text-n-ruby-11"
        >
          <span
            aria-hidden="true"
            class="i-lucide-scissors size-3 shrink-0 mt-0.5"
          />
          {{ t('KANBAN.SUMMARY.PARTIAL_WARNING') }}
        </p>

        <p class="m-0 text-sm whitespace-pre-line text-n-slate-12">
          {{ summary.resumo }}
        </p>

        <p v-if="isInputTruncated" class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.SUMMARY.TRUNCATED_INPUT') }}
        </p>

        <div class="flex flex-col gap-1">
          <h4 class="m-0 text-xs font-semibold text-n-slate-11">
            {{ t('KANBAN.SUMMARY.CITATIONS_TITLE') }}
          </h4>
          <p v-if="!citations.length" class="m-0 text-xs text-n-slate-11">
            {{ t('KANBAN.SUMMARY.CITATIONS_EMPTY') }}
          </p>
          <!-- Citação SEM link não entrega a garantia: o valor todo está em o
             agente poder conferir o trecho na mensagem de onde ele saiu. -->
          <ul v-else class="flex flex-col gap-1 m-0 p-0 list-none">
            <li
              v-for="citation in citations"
              :key="citation.message_id"
              data-testid="kanban-summary-citation"
              class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1"
            >
              <q class="text-xs text-n-slate-12">{{ citation.trecho }}</q>
              <NextButton
                link
                slate
                xs
                class="self-start"
                data-testid="kanban-summary-citation-link"
                :label="t('KANBAN.SUMMARY.GO_TO_MESSAGE')"
                @click="emit('goToMessage', citation.message_id)"
              />
            </li>
          </ul>
        </div>

        <p v-if="verificationLabel" class="m-0 text-xs text-n-slate-11">
          {{ verificationLabel }}
        </p>
      </div>
    </div>
  </section>
</template>
